import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';

/**
 * Selection metadata for the html-design animation skills — the category grouping and content tags
 * that organize the "## Animation skills" index so the agent can match a moment's content (a stat, a
 * list, a scene change) to the right technique instead of scanning a flat list of names.
 *
 * Kept beside the registry rather than as fields on each Skill so the definition files stay pure
 * technique content (markup + timeline code + pitfalls) and the shared Skill type stays unpolluted. A
 * registry test asserts this map covers every registered skill, so the two can't drift.
 *
 * A deliberate sibling of codeo's skillMeta: the two agents author against different composition
 * contracts and word their prompts differently, so the scaffolding is duplicated rather than shared.
 */

export type HtmlDesignSkillCategory =
    | 'text'
    | 'data'
    | 'svg'
    | 'layout'
    | 'ambient'
    | 'transitions'
    | 'craft';

// Render order + display label for each category in the system-prompt index.
export const HTML_DESIGN_SKILL_CATEGORIES: { id: HtmlDesignSkillCategory; label: string }[] = [
    { id: 'text', label: 'Text & typography' },
    { id: 'data', label: 'Numbers & data' },
    { id: 'svg', label: 'SVG & icons' },
    { id: 'layout', label: 'Layout & comparison' },
    { id: 'ambient', label: 'Ambient & depth' },
    { id: 'transitions', label: 'Camera & transitions' },
    { id: 'craft', label: 'Polish & finishing' },
];

export interface HtmlDesignSkillMeta {
    category: HtmlDesignSkillCategory;
    // Content/trigger keywords — what a moment is "about" when this technique fits.
    tags: string[];
}

// Keyed by Skill.name. A test in skillRegistry.spec enforces full coverage (every registered skill
// has an entry) and no orphans (no entry without a registered skill).
export const HTML_DESIGN_SKILL_META: Record<string, HtmlDesignSkillMeta> = {
    [SKILL_NAMES.kineticWordReveal]: {
        category: 'text',
        tags: ['headline', 'title', 'text-reveal', 'kinetic-type'],
    },
    [SKILL_NAMES.emphasisWordPulse]: {
        category: 'text',
        tags: ['emphasis', 'keyword', 'accent', 'voice-sync'],
    },
    [SKILL_NAMES.markerEmphasis]: {
        category: 'text',
        tags: ['highlight', 'underline', 'marker', 'emphasis'],
    },
    [SKILL_NAMES.staggeredListReveal]: {
        category: 'text',
        tags: ['list', 'steps', 'bullets', 'cascade', 'sequence'],
    },
    [SKILL_NAMES.counterTickUp]: {
        category: 'data',
        tags: ['number', 'stat', 'count-up', 'metric'],
    },
    [SKILL_NAMES.statBarsAndFills]: {
        category: 'data',
        tags: ['stat', 'bar-chart', 'comparison', 'progress'],
    },
    [SKILL_NAMES.motionPolish]: {
        category: 'craft',
        tags: ['easing', 'overlap', 'stagger', 'timing', 'choreography', 'polish'],
    },
    [SKILL_NAMES.grainAndBloom]: {
        category: 'craft',
        tags: ['grain', 'texture', 'bloom', 'glow', 'vignette', 'finishing', 'film'],
    },
    [SKILL_NAMES.svgDrawOn]: {
        category: 'svg',
        tags: ['svg', 'line-art', 'draw-on', 'diagram'],
    },
    [SKILL_NAMES.svgIconEnrichment]: {
        category: 'svg',
        tags: ['icon', 'svg', 'symbol', 'accent'],
    },
    [SKILL_NAMES.ambientFloatLoop]: {
        category: 'ambient',
        tags: ['ambient', 'idle', 'float', 'background', 'loop'],
    },
    [SKILL_NAMES.parallaxDepthDrift]: {
        category: 'ambient',
        tags: ['parallax', 'depth', 'layers', 'background', 'drift'],
    },
    [SKILL_NAMES.atmosphereGradientField]: {
        category: 'ambient',
        tags: ['background', 'atmosphere', 'gradient', 'ambient', 'depth'],
    },
    [SKILL_NAMES.ambientParticleField]: {
        category: 'ambient',
        tags: ['background', 'particles', 'ambient', 'depth', 'dust', 'stars'],
    },
    [SKILL_NAMES.cinematicCameraMove]: {
        category: 'transitions',
        tags: ['camera', 'push', 'pan', 'zoom', 'cinematic'],
    },
    [SKILL_NAMES.momentSweepTransition]: {
        category: 'transitions',
        tags: ['transition', 'sweep', 'scene-change', 'moment'],
    },
    [SKILL_NAMES.velocityMatchedCut]: {
        category: 'transitions',
        tags: ['transition', 'cut', 'velocity', 'continuous-motion'],
    },
    [SKILL_NAMES.elementMorphHandoff]: {
        category: 'transitions',
        tags: ['transition', 'morph', 'handoff', 'continuous'],
    },
    [SKILL_NAMES.splitComparisonCards]: {
        category: 'layout',
        tags: ['comparison', 'versus', 'two-up', 'before-after', 'cards'],
    },
};
